const PP_CART_ADD_PATH = 'cart/add.js';
const PP_SELECTED_PILL_CLASS = 'perfect-pairing-upsell__pill--selected';
const PP_LOADING_CLASS = 'loading';
const PP_HIDDEN_CLASS = 'hidden';
const PP_UPSELL_SOURCE = 'cart-perfect-pairing-upsell';
const PP_ARROW_KEYS_NEXT = ['ArrowRight', 'ArrowDown'];
const PP_ARROW_KEYS_PREV = ['ArrowLeft', 'ArrowUp'];

class PerfectPairingUpsell extends HTMLElement {
  constructor() {
    super();
    this.pills = [];
    this.addButton = null;
    this.spinner = null;
    this.subtitleEl = null;
    this.priceEl = null;
    this.titleEl = null;
    this.imageEl = null;
    this.selectedVariantId = '';
    this.selectedSellingPlanId = '';
    this.boundPillClick = this.handlePillClick.bind(this);
    this.boundPillKeydown = this.handlePillKeydown.bind(this);
    this.boundAddClick = this.handleAddClick.bind(this);
  }

  connectedCallback() {
    this.addButton = this.querySelector('[data-pp-add]');
    if (!this.addButton) return;

    this.spinner = this.querySelector('[data-pp-spinner]');
    this.subtitleEl = this.querySelector('[data-pp-subtitle]');
    this.priceEl = this.querySelector('[data-pp-price]');
    this.priceFinalEl = this.querySelector('[data-pp-price-final]');
    this.priceOriginalEl = this.querySelector('[data-pp-price-original]');
    this.titleEl = this.querySelector('[data-pp-title]');
    this.imageEl = this.querySelector('.perfect-pairing-upsell__image');
    this.pills = Array.from(this.querySelectorAll('[data-pp-pill]'));

    // Seed the add-to-cart target from the server-rendered default (root attrs).
    this.selectedVariantId = this.dataset.variantId || '';
    this.selectedSellingPlanId = this.dataset.sellingPlanId || '';

    this.pills.forEach((pill) => {
      pill.addEventListener('click', this.boundPillClick);
      pill.addEventListener('keydown', this.boundPillKeydown);
    });

    // If pills exist, seed roving tabindex and sync the target to the
    // pre-selected (first) pill.
    if (this.pills.length) {
      const preselected = this.pills.find((pill) => pill.classList.contains(PP_SELECTED_PILL_CLASS)) || this.pills[0];
      this.applyPillSelection(preselected, true);
    }

    this.addButton.addEventListener('click', this.boundAddClick);
  }

  disconnectedCallback() {
    this.pills.forEach((pill) => {
      pill.removeEventListener('click', this.boundPillClick);
      pill.removeEventListener('keydown', this.boundPillKeydown);
    });
    this.addButton?.removeEventListener('click', this.boundAddClick);
  }

  handlePillClick(event) {
    this.applyPillSelection(event.currentTarget, true);
  }

  handlePillKeydown(event) {
    if (PP_ARROW_KEYS_NEXT.includes(event.key) || PP_ARROW_KEYS_PREV.includes(event.key)) {
      event.preventDefault();
      this.moveSelection(event.currentTarget, PP_ARROW_KEYS_NEXT.includes(event.key) ? 1 : -1);
    }
  }

  moveSelection(currentPill, direction) {
    const currentIndex = this.pills.indexOf(currentPill);
    if (currentIndex === -1) return;

    const nextIndex = (currentIndex + direction + this.pills.length) % this.pills.length;
    const nextPill = this.pills[nextIndex];
    this.applyPillSelection(nextPill, true);
    nextPill.focus();
  }

  applyPillSelection(pill, updateFocusability) {
    this.pills.forEach((item) => {
      const isSelected = item === pill;
      item.classList.toggle(PP_SELECTED_PILL_CLASS, isSelected);
      item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      // Roving tabindex: only the selected pill is in the tab order.
      if (updateFocusability) {
        item.tabIndex = isSelected ? 0 : -1;
      }
    });

    this.selectedVariantId = pill.dataset.variantId || this.selectedVariantId;
    this.selectedSellingPlanId = pill.dataset.sellingPlanId || this.selectedSellingPlanId;

    if (this.subtitleEl && pill.dataset.planLabel) {
      this.subtitleEl.textContent = pill.dataset.planLabel;
    }
    // Update the discounted (final) and struck-through (original) prices separately
    // so the price markup stays intact. data-price = final, data-price-original = full.
    if (this.priceFinalEl && pill.dataset.price) {
      this.priceFinalEl.textContent = pill.dataset.price;
    }
    if (this.priceOriginalEl && pill.dataset.priceOriginal) {
      this.priceOriginalEl.textContent = pill.dataset.priceOriginal;
      this.priceOriginalEl.classList.toggle('hidden', pill.dataset.price === pill.dataset.priceOriginal);
    }
    // Title + image change when the FO product differs between pills (8oz vs 16oz).
    if (this.titleEl && pill.dataset.productTitle) {
      this.titleEl.textContent = pill.dataset.productTitle;
    }
    if (this.imageEl && pill.dataset.image) {
      this.imageEl.src = pill.dataset.image;
      // Drop srcset so the single chosen src is used (avoids a stale 8oz srcset).
      this.imageEl.removeAttribute('srcset');
      if (pill.dataset.productTitle) {
        this.imageEl.alt = pill.dataset.productTitle;
      }
    }
  }

  handleAddClick(event) {
    event.preventDefault();
    if (this.addButton.disabled || this.addButton.classList.contains(PP_LOADING_CLASS)) return;
    if (!this.selectedVariantId) return;

    this.addToCart(this.selectedVariantId, this.selectedSellingPlanId);
  }

  buildCartData(variantId, sellingPlanId) {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartData = {
      id: variantId,
      quantity: 1,
      properties: {
        _perfect_pairing_upsell: 'true',
      },
    };

    // Subscription: include selling_plan. OTP: sellingPlanId is empty, selling_plan is omitted.
    if (sellingPlanId && sellingPlanId.trim() !== '') {
      cartData.selling_plan = sellingPlanId;
    }

    if (cartDrawer && cartDrawer.getSectionsToRender) {
      cartData.sections = cartDrawer
        .getSectionsToRender()
        .map((section) => section.id)
        .join(',');
      cartData.sections_url = window.location.pathname;
    }

    return cartData;
  }

  async addToCart(variantId, sellingPlanId) {
    try {
      this.setLoadingState(true);

      const response = await window.cartFetchWithRetry(window.Shopify.routes.root + PP_CART_ADD_PATH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(this.buildCartData(variantId, sellingPlanId)),
      });

      /*
       * A throttled request comes back as HTML, so .json() would throw a SyntaxError that
       * reads as a code bug. Throw explicitly and route through the catch → handleError(),
       * which by design shows nothing; the finally below restores the button either way.
       * 422 keeps its JSON error body and its existing handleError(data) path.
       */
      if (!response.ok && response.status !== 422) {
        throw new Error(`Cart add failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 422 || data.status === 'error') {
        this.handleError(data);
      } else {
        this.handleSuccess(variantId, data);
      }
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  handleSuccess(variantId, data) {
    // Re-render the cart drawer FIRST so the visual update is guaranteed: the
    // Liquid self-clearing gate then removes this card (cart now holds 2 distinct
    // products). This must run before publish() — see the try/catch note below.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.renderContents && data.sections) {
      cartDrawer.renderContents(data);
    }

    // The cart/add.js response is the ADDED line item (no `items`/`item_count`),
    // not a full cart. Some cartUpdate subscribers assume a full cart and call
    // `cartData.items.reduce(...)`, which throws on this payload. Guard the publish
    // so a third-party subscriber's error can't break our own flow.
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish !== 'undefined') {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: PP_UPSELL_SOURCE,
          productVariantId: variantId,
          cartData: data,
        });
      } catch (error) {
        // Swallow: a third-party cartUpdate subscriber can throw on the add.js payload
        // (it's the ADDED line item, not a full cart). Our own flow already re-rendered.
      }
    }
  }

  handleError() {
    // Add-to-cart failed; restore the button to its idle state (done in setLoadingState's
    // finally). No user-facing error UI is required for this upsell.
  }

  setLoadingState(isLoading) {
    if (!this.addButton) return;

    this.addButton.classList.toggle(PP_LOADING_CLASS, isLoading);
    this.addButton.disabled = isLoading;
    this.spinner?.classList.toggle(PP_HIDDEN_CLASS, !isLoading);
  }
}

if (!customElements.get('perfect-pairing-upsell')) {
  customElements.define('perfect-pairing-upsell', PerfectPairingUpsell);
}
